import { createAsyncThunk } from "@reduxjs/toolkit";
import customFetch from "../../util/config/axiosConfig";
import {
    BillingClientsResponse,
    CreditLine,
    EmbeddedConfig,
    SignupState,
} from "../../types/EmbeddedSignupType";

const BASE = "/api/embedded-signup"; // customFetch baseURL is assumed to already include /api
const ADMIN = "/api/admin/embedded-signup";

// ---------- helpers ----------
const parse = (v: any) => (typeof v === "string" ? JSON.parse(v) : v);

/** Unwraps { status: 1, data } and throws on { status: 0, error } */
const unwrap = (response: any) => {
    const payload = parse(response.data);
    if (payload?.status === 0) {
        throw { error: payload.error };
    }
    return parse(payload?.data);
};

export const getErrorMessage = (e: any, fallback: string): string =>
    e?.response?.data?.error?.message ||
    e?.error?.message ||
    e?.message ||
    fallback;

type Cfg = { rejectValue: string };

// ---------- client ----------
export const fetchEmbeddedConfig = createAsyncThunk<EmbeddedConfig, void, Cfg>(
    "embeddedSignup/config",
    async (_, thunkAPI) => {
        try {
            return unwrap(await customFetch.get(`${BASE}/config`));
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Failed to load configuration"));
        }
    }
);

export const fetchEmbeddedStatus = createAsyncThunk<SignupState, void, Cfg>(
    "embeddedSignup/status",
    async (_, thunkAPI) => {
        try {
            return unwrap(await customFetch.get(`${BASE}/status`));
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Failed to load status"));
        }
    }
);

export const connectEmbeddedSignup = createAsyncThunk<
    SignupState,
    { code: string; wabaId?: string | null; phoneNumberId?: string | null },
    Cfg
>("embeddedSignup/connect", async (body, thunkAPI) => {
    try {
        return unwrap(
            await customFetch.post(`${BASE}/connect`, {
                code: body.code,
                wabaId: body.wabaId ?? null,
                phoneNumberId: body.phoneNumberId ?? null,
            })
        );
    } catch (e) {
        return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not connect WhatsApp"));
    }
});

export const selectEmbeddedNumber = createAsyncThunk<
    SignupState,
    { phoneNumberId: string },
    Cfg
>("embeddedSignup/number", async (body, thunkAPI) => {
    try {
        return unwrap(await customFetch.post(`${BASE}/number`, body));
    } catch (e) {
        return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not use this number"));
    }
});

export const submitEmbeddedPin = createAsyncThunk<SignupState, { pin: string }, Cfg>(
    "embeddedSignup/pin",
    async (body, thunkAPI) => {
        try {
            return unwrap(await customFetch.post(`${BASE}/pin`, body));
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not verify the PIN"));
        }
    }
);

export const retryEmbeddedSignup = createAsyncThunk<SignupState, void, Cfg>(
    "embeddedSignup/retry",
    async (_, thunkAPI) => {
        try {
            return unwrap(await customFetch.post(`${BASE}/retry`));
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Retry failed"));
        }
    }
);

export const sendEmbeddedTestMessage = createAsyncThunk<
    SignupState,
    { mobileNumber: string },
    Cfg
>("embeddedSignup/testMessage", async (body, thunkAPI) => {
    try {
        return unwrap(await customFetch.post(`${BASE}/test-message`, body));
    } catch (e) {
        return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not send the test message"));
    }
});

export const disconnectEmbeddedSignup = createAsyncThunk<SignupState | null, void, Cfg>(
    "embeddedSignup/disconnect",
    async (_, thunkAPI) => {
        try {
            return unwrap(await customFetch.post(`${BASE}/disconnect`)) ?? null;
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not disconnect"));
        }
    }
);

// ---------- admin (ROLE_ADMIN) ----------
export const fetchCreditLines = createAsyncThunk<CreditLine[], void, Cfg>(
    "embeddedSignup/admin/creditLines",
    async (_, thunkAPI) => {
        try {
            const data = unwrap(await customFetch.get(`${ADMIN}/credit-lines`));
            return Array.isArray(data) ? data : data?.results ?? [];
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Failed to load credit lines"));
        }
    }
);

export const fetchBillingClients = createAsyncThunk<
    BillingClientsResponse,
    { page?: number; size?: number } | undefined,
    Cfg
>("embeddedSignup/admin/clients", async (params, thunkAPI) => {
    try {
        const page = params?.page ?? 0;
        const size = params?.size ?? 20;
        return unwrap(await customFetch.get(`${ADMIN}/clients?page=${page}&size=${size}`));
    } catch (e) {
        return thunkAPI.rejectWithValue(getErrorMessage(e, "Failed to load clients"));
    }
});

export const shareCredit = createAsyncThunk<unknown, number, Cfg>(
    "embeddedSignup/admin/shareCredit",
    async (userId, thunkAPI) => {
        try {
            return unwrap(await customFetch.post(`${ADMIN}/clients/${userId}/share-credit`));
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not share credit"));
        }
    }
);

export const revokeCredit = createAsyncThunk<unknown, number, Cfg>(
    "embeddedSignup/admin/revokeCredit",
    async (userId, thunkAPI) => {
        try {
            return unwrap(await customFetch.post(`${ADMIN}/clients/${userId}/revoke-credit`));
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not revoke credit"));
        }
    }
);

export const shareCreditAll = createAsyncThunk<unknown, void, Cfg>(
    "embeddedSignup/admin/shareCreditAll",
    async (_, thunkAPI) => {
        try {
            return unwrap(await customFetch.post(`${ADMIN}/share-credit-all`));
        } catch (e) {
            return thunkAPI.rejectWithValue(getErrorMessage(e, "Could not share credit with all clients"));
        }
    }
);